import { useMemo, useState } from 'react';
import { Checkbox, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import styles from './OptionList.module.css';

export interface FilterOption {
  value: string;
  label: string;
  /** Сколько записей даёт значение; показывается рядом с названием. */
  count?: number;
}

// Сгруппированные опции (напр. форматы по «Платформа — Лента»).
export interface FilterOptionGroup {
  label: string;
  options: FilterOption[];
}

interface OptionListProps {
  // Либо плоский список опций, либо сгруппированный.
  options?: FilterOption[];
  groups?: FilterOptionGroup[];
  value: string[];
  onChange: (v: string[]) => void;
  searchLabel: string;
}

// Список с галочками, поиском и «выбрать/убрать все». Отмеченное — черновик
// фильтра: наружу уходит только по «Применить» панели.
export function OptionList({ options, groups, value, onChange, searchLabel }: OptionListProps) {
  const [query, setQuery] = useState('');

  // Плоский список нормализуем в одну безымянную секцию.
  const sections: FilterOptionGroup[] = useMemo(
    () => groups ?? [{ label: '', options: options ?? [] }],
    [groups, options],
  );
  const total = useMemo(() => sections.reduce((n, s) => n + s.options.length, 0), [sections]);

  const ql = query.trim().toLowerCase();
  const visibleSections = useMemo(() => {
    if (!ql) return sections;
    return sections
      .map((s) => ({ ...s, options: s.options.filter((o) => o.label.toLowerCase().includes(ql)) }))
      .filter((s) => s.options.length > 0);
  }, [sections, ql]);
  const visibleValues = useMemo(
    () => visibleSections.flatMap((s) => s.options.map((o) => o.value)),
    [visibleSections],
  );

  const toggle = (v: string, checked: boolean) =>
    onChange(checked ? [...value, v] : value.filter((x) => x !== v));

  // «Выбрать все»/«Убрать все» работают по видимым (с учётом поиска) опциям;
  // без поиска это весь список.
  const selectAll = () => onChange(Array.from(new Set([...value, ...visibleValues])));
  const clearAll = () => {
    const vis = new Set(visibleValues);
    onChange(value.filter((x) => !vis.has(x)));
  };

  return (
    <div>
      {total > 8 && (
        <Input
          size="small"
          prefix={<SearchOutlined className={styles.searchIcon} />}
          placeholder="Найти значение"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          allowClear
          className={styles.search}
          aria-label={searchLabel}
        />
      )}

      <div className={styles.toolbar}>
        <span className={styles.counter}>
          Выбрано {value.length} из {total}
        </span>
        <div className={styles.toolbarBtns}>
          <button type="button" className={styles.linkBtn} onClick={selectAll}>
            Выбрать все
          </button>
          <button type="button" className={styles.linkBtn} onClick={clearAll}>
            Убрать все
          </button>
        </div>
      </div>

      <div className={styles.list}>
        {visibleSections.length === 0 ? (
          <div className={styles.empty}>Ничего не найдено</div>
        ) : (
          visibleSections.map((s, i) => (
            <div key={s.label || i} className={styles.group}>
              {s.label && <div className={styles.groupLabel}>{s.label}</div>}
              {s.options.map((o) => (
                <Checkbox
                  key={o.value}
                  className={styles.option}
                  checked={value.includes(o.value)}
                  onChange={(e) => toggle(o.value, e.target.checked)}
                >
                  {o.label}
                  {o.count !== undefined && <span className={styles.count}>{o.count}</span>}
                </Checkbox>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
